import React, { useState, useRef, useEffect } from 'react';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { GrClose } from 'react-icons/gr';
import BackButton from '../BackButton/BackButton';
import SearchInput from '../SearchInput/SearchInput';
import MenuBoldItem from '../MenuItems/MenuBoldItem/MenuBoldItem';
import MenuNormalItem from '../MenuItems/MenuNormalItem/MenuNormalItem';
import { MdArrowForwardIos } from 'react-icons/md';
import { BiWorld } from 'react-icons/bi';
import { useMediaQuery } from 'react-responsive';
import SocialNetworks from '../SocialNetworks/SocialNetworks';
import { BsCheck2 } from 'react-icons/bs';
import data from '../../data/data';
import settings from '../../data/settings';
import ConvertHTMLString from '../../helperFunctions/ConvertedHTMLString';

const MenuContentMultipage = ({ onClick, isOpen, setIsOpen }) => {
  const isSmallScreen = useMediaQuery({ maxWidth: 640 });
  const isMediumScreen = useMediaQuery({ minWidth: 641, maxWidth: 1023 });
  const [menuItems, setMenuItems] = useState(data.menu_items);
  const [menuItemsFirstLevel, setMenuItemsFirstLevel] = useState([]);
  const [menuItemsSecondLevel, setMenuItemsSecondLevel] = useState([]);
  const [menuItemsThirdLevel, setMenuItemsThirdLevel] = useState([]);
  const [menuItemsFourthLevel, setMenuItemsFourthLevel] = useState([]);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [language, setLanguage] = useState('');
  const [searchInfo, setSearchInfo] = useState('');
  const [selectedLanguageInitials, setSelectedLanguageInitials] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [whichMenuLevel, setWhichMenuLevel] = useState('1');
  const [bgColor, setBgColor] = useState('');
  const [customHTML, setCustomHTML] = useState(null);

  const [menuBackgroundColor, setMenuBackgroundColor] = useState('#fff');
  const [isLanguageSwitcherVisible, setIsLanguageSwitcherVisible] = useState(
    settings.lang_switcher_visibility
  );
  const [closeButtonAlignment, setCloseButtonAlignment] = useState('end');
  const [socialNetworksAlignment, setSocialNetworksAlignment] = useState('end');

  const [navigationContentAlignment, setNavigationContentAlignment] =
    useState(null);

  const [isButtonAnimated, setIsButtonAnimated] = useState(false);

  useEffect(() => {
    setCustomHTML(ConvertHTMLString(data.custom_html));
    setIsLanguageSwitcherVisible(settings.lang_switcher_visibility);
  }, []);

  const [hover, setHover] = useState(false);
  const hoverStyle = {
    color: hover
      ? settings.colors.link_hover_color
      : settings.colors.font_color_regular,
  };

  const ref = useRef();

  const url = data.search_url;

  useOnClickOutside(ref, () => {
    setIsLanguageMenuOpen(false);
  });

  const handleOnChange = (e) => {
    setSearchInfo(e.target.value);
  };

  const handleClick = () => {
    window.location = url.replace('{needle}', `${searchInfo}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      window.location = url.replace('{needle}', `${searchInfo}`);
    }
  };

  useEffect(() => {
    const refactorSelectedLang = (lang) => {
      return lang.replace('_', '-');
    };

    const choseLanguageByLangInitials = (languageInitials) => {
      if (data.available_languages.length === 0) {
        console.log('There are no available languages!');
        return; // Explicitly return undefined or you could choose to return null.
      }

      const result = data.available_languages.find(
          (language) => language.lang.split('-')[0] === languageInitials.split('-')[0]
      );

      if (result) {
        return result.name;
      } else {
        console.error('No matching language found!');
        return null; // Or another appropriate default value.
      }
    };

    setAvailableLanguages(data.available_languages);
    const selectedLanguageInitials = refactorSelectedLang(data.selected_lang);
    setSelectedLanguageInitials(selectedLanguageInitials);
    const chosenLanguage = choseLanguageByLangInitials(
      selectedLanguageInitials
    );
    setLanguage(chosenLanguage);
  }, []);

  const setBackgroundColor = () => {
    setTimeout(() => {
      setBgColor('rgba(0, 0, 0, 0.3)');
    }, 500);
  };

  const closeMenu = () => {
    setBgColor('transparent');
    setTimeout(() => {
      setMenuItems(data.menu_items);
    }, 500);
  };

  useEffect(() => {
    isOpen ? setBackgroundColor() : closeMenu();
  }, [isOpen]);

  const modalStyle = {
    overlay: {
      position: 'fixed',
      transition: '0.5s ease-out, transform 0.25s ease-out',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: `${bgColor}`,
      zIndex: 1,
    },
    content: {
      top: 0,
      left: 0,
      bottom: 0,
      transition: 'transform 0.5s ease-out',
      transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
      maxWidth: `${
        isSmallScreen ? '100%' : isMediumScreen ? '480px' : '600px'
      }`,
      marginRight: 'auto',
      border: 'none',
      background: `${menuBackgroundColor}`,
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      borderRadius: '4px',
      outline: 'none',
      paddingTop: 20,
      paddingLeft: 0,
      paddingBottom: 0,
      paddingRight: 0,
    },
  };

  useEffect(() => {
    setMenuBackgroundColor(settings.colors.navigation_background_color);
    setIsButtonAnimated(settings.animated_menu_button);
  }, []);

  useEffect(() => {
    // Close button alignment
    if (settings.alignment.close_navigation_button === 'right') {
      setCloseButtonAlignment('end');
    } else {
      setCloseButtonAlignment('start');
    }

    // Social networks alignment
    if (settings.alignment.social_icons === 'right') {
      setSocialNetworksAlignment('end');
    } else if (settings.alignment.social_icons === 'center') {
      setSocialNetworksAlignment('center');
    } else {
      setSocialNetworksAlignment('start');
    }

    // Customizing back arrow alignment
    if (settings.alignment.navigation_content === 'left') {
      setNavigationContentAlignment('left');
    }
    if (settings.alignment.navigation_content === 'center') {
      setNavigationContentAlignment('center');
    }
    if (settings.alignment.navigation_content === 'right') {
      setNavigationContentAlignment('right');
    }
  }, []);

  return (
    <div
      className={`flex flex-col justify-between h-full viergrad-custom-menu-font-regular ${
        settings.searchbar_visibility ? 'pt-16' : '0'
      }`}
    >
      <div className='flex flex-col justify-start w-full'>
        {!isButtonAnimated ? (
          <div
            // className='self-end pr-[30px] sm:pr-[24px] hover:cursor-pointer'
            className={`self-${closeButtonAlignment} px-[30px] sm:px-[24px] mt-[-2.2rem] sm:mt-[-2.5rem] hover:cursor-pointer`}
            onClick={onClick}
          >
            <GrClose fontSize='1.5rem' />
          </div>
        ) : null}

        {menuItems ? (
          whichMenuLevel === '1' ? (
            <div className='mx-8 pt-8 sm:pl-16 sm:pr-16'>
              <SearchInput
                onChange={handleOnChange}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
              />
            </div>
          ) : (
            <div
              className='mx-8 pt-8 sm:pl-12 sm:pr-16'
              style={{
                display: 'flex',
                flexDirection:
                  navigationContentAlignment === 'right'
                    ? 'row-reverse'
                    : navigationContentAlignment === 'left'
                    ? 'row'
                    : 'row',
                justifyContent:
                  navigationContentAlignment === 'right'
                    ? 'space-between'
                    : navigationContentAlignment === 'left'
                    ? 'space-between'
                    : 'center',
              }}
            >
              {whichMenuLevel === '5' ? (
                <BackButton
                  onClick={() => {
                    setMenuItems(menuItemsFourthLevel);
                    setWhichMenuLevel('4');
                  }}
                />
              ) : whichMenuLevel === '4' ? (
                <BackButton
                  onClick={() => {
                    setMenuItems(menuItemsThirdLevel);
                    setWhichMenuLevel('3');
                  }}
                />
              ) : whichMenuLevel === '3' ? (
                <BackButton
                  onClick={() => {
                    setMenuItems(menuItemsSecondLevel);
                    setWhichMenuLevel('2');
                  }}
                />
              ) : whichMenuLevel === '2' ? (
                <BackButton
                  onClick={() => {
                    setMenuItems(menuItemsFirstLevel);
                    setWhichMenuLevel('1');
                  }}
                />
              ) : (
                ''
              )}
            </div>
          )
        ) : (
          ''
        )}
        <div className='pt-14 sm:pl-16 sm:pr-16'>
          {menuItems
            ? menuItems.map((item) => {
                return item.multiLevel && item.submenuFirstLevel ? (
                  <MenuBoldItem
                    name={item.name}
                    multilevel={item.multiLevel}
                    key={item.id}
                    onClick={() => {
                      setMenuItems(item.submenuFirstLevel);
                      setMenuItemsFirstLevel(data.menu_items);
                      setWhichMenuLevel('2');
                    }}
                    href={item.href}
                    target={item.target}
                  />
                ) : item.submenuSecondLevel ? (
                  <MenuNormalItem
                    name={item.name}
                    key={item.id}
                    submenuSecondLevel={item.submenuSecondLevel}
                    href={item.href}
                    target={item.target}
                    onClick={() => {
                      setMenuItemsSecondLevel(menuItems);
                      setMenuItems(item.submenuSecondLevel);
                      setWhichMenuLevel('3');
                    }}
                  />
                ) : item.submenuThirdLevel ? (
                  <MenuNormalItem
                    name={item.name}
                    key={item.id}
                    submenuThirdLevel={item.submenuThirdLevel}
                    href={item.href}
                    target={item.target}
                    onClick={() => {
                      setMenuItemsThirdLevel(menuItems);
                      setMenuItems(item.submenuThirdLevel);
                      setWhichMenuLevel('4');
                    }}
                  />
                ) : (
                  <MenuNormalItem
                    name={item.name}
                    key={item.id}
                    submenuFourthLevel={item.submenuFourthLevel}
                    href={item.href}
                    target={item.target}
                    onClick={() => {
                      setMenuItemsFourthLevel(menuItems);
                      setMenuItems(item.submenuFourthLevel);
                      setWhichMenuLevel('5');
                    }}
                  />
                );
              })
            : setTimeout(() => setIsOpen(false), 0)}
        </div>
        {/* CUSTOM HTML PART */}
        {whichMenuLevel === '1' ? (
          <div
            dangerouslySetInnerHTML={{ __html: customHTML }}
            className='px-16 mx-8 my-3 custom-html-element'
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent:
                navigationContentAlignment === 'left'
                  ? 'flex-start'
                  : navigationContentAlignment === 'right'
                  ? 'flex-end'
                  : 'center',
            }}
          ></div>
        ) : (
          ''
        )}
      </div>

      {whichMenuLevel === '1' ? (
        <div ref={ref} className='flex flex-col'>
          {!isLanguageMenuOpen ? (
            <div
              className='sm:px-16'
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: socialNetworksAlignment,
              }}
            >
              <SocialNetworks />
            </div>
          ) : (
            <div className='flex flex-col sm:pl-16 py-5 bg-transparent viergrad-custom-menu-font-regular'>
              {data.available_languages.map((language) => {
                return (
                  <div
                    className='flex flex-row justify-start left-2 py-2 mx-8 hover:cursor-pointer'
                    style={hoverStyle}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    onClick={() => {
                      setLanguage(language.name);
                      setSelectedLanguageInitials(language.lang);
                    }}
                  >
                    <div className='flex items-center pt-1 pl-1 pr-3.5 gap-3'>
                      {language.lang === selectedLanguageInitials ? (
                        <BsCheck2 />
                      ) : (
                        <BsCheck2
                          style={{
                            visibility: 'hidden',
                          }}
                        />
                      )}
                    </div>
                    {language.lang === selectedLanguageInitials ? (
                      <div>{language.name}</div>
                    ) : (
                      <a href={language.href} style={{ width: '100%' }}>
                        <div>{language.name}</div>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {availableLanguages.length > 0 || isLanguageSwitcherVisible ? (
            <div
              className='flex flex-col bg-transparent'
              //   style={hoverStyle}
              //   onMouseEnter={() => setHover(true)}
              //   onMouseLeave={() => setHover(false)}
            >
              <div
                //   className='flex flex-row justify-between bg-transparent py-5 text-darkFontColor hover:text-turquoise hover:cursor-pointer'
                className={`flex flex-row justify-between bg-transparent py-5 text-[${settings.colors.font_color_regular}] hover:[${settings.colors.link_hover_color}] hover:cursor-pointer`}
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              >
                <div className='flex flex-row justify-start gap-3 mx-7 sm:pl-16 left-0 right-0 '>
                  <div
                    className='pt-0.25 text-2xl'
                    style={{ color: settings.colors.font_color_regular }}
                    // style={hoverStyle}
                    // onMouseEnter={() => setHover(true)}
                    // onMouseLeave={() => setHover(false)}
                  >
                    <BiWorld />
                  </div>
                  <span
                    className='viergrad-custom-menu-font-regular'
                    style={{ color: settings.colors.font_color_regular }}
                    // style={hoverStyle}
                    // onMouseEnter={() => setHover(true)}
                    // onMouseLeave={() => setHover(false)}
                  >
                    {language}
                  </span>
                </div>
                <div
                  className='pr-7 sm:pr-16 sm:mr-8 pt-0.5 font-bold text-xl'
                  style={{ color: settings.colors.font_color_regular }}
                  //   style={hoverStyle}
                  //   onMouseEnter={() => setHover(true)}
                  //   onMouseLeave={() => setHover(false)}
                >
                  <MdArrowForwardIos />
                </div>
              </div>
            </div>
          ) : (
            ''
          )}
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default MenuContentMultipage;
